import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { radius, rem, space } from '@gbi/ui';
import tokens from '../../assets/styles/tokens';
import Button from '../Button/Button';
import BottomDrawer from '../Drawer/Drawer';
import { ReactComponent as InfoGttIcon } from '../../assets/images/priceAlerts/info-gtt.svg';
import { ModalCloseIcon } from './Common';

const slideDown = keyframes`
  from { transform: translateY(-100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${rem(space[24])};
  z-index: 2000;
`;

// The designer's popup (Buy Wizard - Trigger Order, frame 348): 24px above the
// icon, 16px at the sides and below the button.
const Card = styled.div`
  position: relative;
  background: ${tokens.color.surface.page};
  border-radius: ${radius.lg};
  padding: ${rem(space[24])} ${rem(space[16])} ${rem(space[16])};
  width: 100%;
  max-width: ${rem(420)};
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
  animation: ${slideDown} 180ms ease-out;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${rem(space[16])};
  right: ${rem(space[16])};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${rem(space[16])};
`;

// The title sits 6px above its message.
const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(space[6])};
`;

const Icon = styled(InfoGttIcon)`
  width: 35.75px;
  height: 35.75px;

  path {
    fill: ${tokens.color.border.focus};
  }
`;

const Title = styled.p`
  margin: 0;
  ${tokens.type.title[400]}
  color: ${tokens.color.text.primary};
`;

const Body = styled.p`
  margin: 0;
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};
`;

const SupportLink = styled.a`
  ${tokens.type.body[300]}
  color: ${tokens.color.text.brand};
  text-decoration: underline;
`;

const CloseActionButton = styled(Button)`
  width: 100%;

  && {
    ${tokens.type.title[400]}
  }
`;

const ReadOnlyAccessModal = ({ open, onClose, openAtTop }) => {
  const { t } = useTranslation();

  // Only the top-of-window variant is a full-screen portal that needs to lock
  // body scroll; the drawer variant lives inside the already-open modal.
  useEffect(() => {
    if (!open || !openAtTop) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open, openAtTop]);

  const phone = t('support.customerServicePhoneGS');
  const phoneDigits = phone.replace(/[^\d+]/g, '');

  const content = (
    <Content data-cy="read-only-access-modal">
      <Icon />
      <TextGroup>
        <Title>{t('gtt.readOnly.modalTitle')}</Title>
        <Body>{t('gtt.readOnly.modalBody')}</Body>
      </TextGroup>
      <SupportLink
        href={`tel:${phoneDigits}`}
        data-cy="link-read-only-access-support"
      >
        {t('gtt.readOnly.supportLink', { phone })}
      </SupportLink>
      <CloseActionButton
        variant="primaryOutlined"
        height="44px"
        textTransform="none"
        onClick={onClose}
        data-cy="button-read-only-access-close"
      >
        {t('common.close')}
      </CloseActionButton>
    </Content>
  );

  // Opened standalone from the disabled bell icon: a top-of-window overlay.
  if (openAtTop) {
    if (!open) return null;
    return ReactDOM.createPortal(
      <Overlay onClick={onClose}>
        <Card onClick={(e) => e.stopPropagation()}>
          <CloseButton
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            data-cy="read-only-access-close-icon"
          >
            <ModalCloseIcon />
          </CloseButton>
          {content}
        </Card>
      </Overlay>,
      document.body,
    );
  }

  // Opened from within the AllAlertsList modal: slide up from its bottom.
  return (
    <BottomDrawer isOpen={open} onClose={onClose}>
      {content}
    </BottomDrawer>
  );
};

ReadOnlyAccessModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  openAtTop: PropTypes.bool,
};

export default ReadOnlyAccessModal;
